import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import Menu from '@mui/material/Menu';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { MENU_COLORS } from '../alliance/bar-menu';
import { CURRENT_APP, CURRENT_APP_FONTS } from '../alliance/current-app';
import { usePermissions } from '../../hooks/use-permissions';
import { NavDataLink, NavDataSection } from './nav-data';
import { NavSectionLink } from './nav-section-link';

/** Section-level permission checks (the links check their own) */
export function useCanViewSection() {
  // Hooks
  const { checkPermissions, checkBillingAccess, hasGroup } = usePermissions();

  return (item: NavDataSection) => {
    let canView = true;
    if (item.hasGroup) {
      // User must have this specific group to view link
      canView = hasGroup(item.hasGroup);
    } else if (item.permissions) {
      // User must have one of the permissions
      canView = checkPermissions(item.permissions);
    }

    let hideFromBilling = false;
    if (item.name && item.access) {
      // Only Billing Navigation items with name and access will be hidden and checked
      hideFromBilling = !checkBillingAccess(item.name, item.access);
    }

    return canView && !hideFromBilling;
  };
}

/** The current page is one of this section's links (or nested links) */
const linksMatch = (params: {
  links: NavDataLink[];
  pathname: string;
}): boolean =>
  params.links.some(
    (link) =>
      (link.to !== undefined &&
        (params.pathname === link.to ||
          params.pathname.startsWith(`${link.to}/`))) ||
      (link.children !== undefined &&
        linksMatch({ links: link.children, pathname: params.pathname })),
  );

/** Whether the current page belongs to this nav section */
const isSectionActive = (params: { item: NavDataSection; pathname: string }) =>
  linksMatch({ links: params.item.links, pathname: params.pathname });

const menuId = (params: { title: string }) =>
  params.title.trim().replace(/\s/g, '').toLowerCase() + '-menu';

// Link rows inside the dropdowns and the drawer: rounded, 40px
const linkRowSx = {
  '& .MuiListItem-root, & .MuiListItemButton-root': {
    minHeight: 40,
    borderRadius: '8px',
    mx: 1,
    width: 'auto',
    '&:hover': { backgroundColor: MENU_COLORS.hover },
  },
  '& .MuiListItemText-primary': { fontSize: 14 },
  '& .MuiSvgIcon-root': { color: MENU_COLORS.text2 },
};

/**
 * One top-nav section on desktop: a tab in the App Bar (accent underline when
 * the current page is in it) that opens its links in a dropdown menu.
 */
export function NavSectionTab(props: { item: NavDataSection }) {
  // Props
  const { item } = props;
  // State
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  // Hooks
  const location = useLocation();
  const canViewSection = useCanViewSection();

  React.useEffect(() => {
    // Close the menu once a link has been followed
    setAnchorEl(null);
  }, [location.pathname]);

  if (!canViewSection(item)) {
    return null;
  }

  const id = menuId({ title: item.title });
  const isActive = isSectionActive({ item, pathname: location.pathname });
  const open = Boolean(anchorEl);

  return (
    <React.Fragment>
      <ButtonBase
        focusRipple
        onClick={(event) => setAnchorEl(event.currentTarget)}
        aria-controls={open ? id : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        aria-current={isActive ? 'page' : undefined}
        sx={{
          fontFamily: 'inherit',
          ...((CURRENT_APP_FONTS.nav as React.CSSProperties | null) || {}),
          height: '100%',
          px: '14px',
          gap: '2px',
          boxSizing: 'border-box',
          borderTop: '3px solid transparent',
          borderBottom: `3px solid ${
            isActive ? CURRENT_APP.accentOnBar : 'transparent'
          }`,
          color: isActive || open ? '#ffffff' : '#ffffffcc',
          fontSize: 15,
          whiteSpace: 'nowrap',
          transition: 'color 0.15s',
          '&:hover': { color: '#ffffff' },
          '&.Mui-focusVisible': {
            outline: '2px solid #ffffff',
            outlineOffset: -4,
          },
        }}
      >
        {item.title}
        <ExpandMore sx={{ fontSize: 18, opacity: 0.7 }} />
      </ButtonBase>

      <Menu
        id={id}
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        onClick={(event) => {
          // Close after an external link opens in a new tab (route links
          // close through the pathname change above)
          if ((event.target as HTMLElement).closest('a')) {
            setAnchorEl(null);
          }
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: '4px',
            minWidth: 240,
            maxHeight: 'calc(100vh - 96px)',
            backgroundColor: MENU_COLORS.paper,
            backgroundImage: 'none',
            color: MENU_COLORS.text,
            border: `1px solid ${MENU_COLORS.border}`,
            borderRadius: '14px',
            boxShadow: '0 12px 32px #0000003d',
            ...linkRowSx,
          },
        }}
      >
        {item.links.map((link) => (
          <NavSectionLink key={link.title} item={link} />
        ))}
      </Menu>
    </React.Fragment>
  );
}

/**
 * One top-nav section in the drawer below md: a 48px row that expands to
 * its links. The section holding the current page starts open.
 */
export function NavSectionGroup(props: { item: NavDataSection }) {
  // Props
  const { item } = props;
  // Hooks
  const location = useLocation();
  const canViewSection = useCanViewSection();

  const isActive = isSectionActive({ item, pathname: location.pathname });
  // State
  const [open, setOpen] = React.useState(isActive);

  if (!canViewSection(item)) {
    return null;
  }

  const id = menuId({ title: item.title });

  return (
    <Box>
      <ButtonBase
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open ? 'true' : 'false'}
        aria-controls={id}
        sx={{
          fontFamily: 'inherit',
          ...((CURRENT_APP_FONTS.nav as React.CSSProperties | null) || {}),
          width: '100%',
          height: 48,
          px: 2,
          justifyContent: 'space-between',
          borderRadius: '10px',
          fontSize: 16,
          color: MENU_COLORS.text,
          backgroundColor: isActive ? '#ffffff14' : 'transparent',
          '&:hover': { backgroundColor: MENU_COLORS.hover },
        }}
      >
        {item.title}
        {open ? (
          <ExpandLess sx={{ fontSize: 20, color: MENU_COLORS.text2 }} />
        ) : (
          <ExpandMore sx={{ fontSize: 20, color: MENU_COLORS.text2 }} />
        )}
      </ButtonBase>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <List id={id} dense component="div" sx={{ py: 0.5, ...linkRowSx }}>
          {item.links.map((link) => (
            <NavSectionLink key={link.title} item={link} />
          ))}
        </List>
      </Collapse>
    </Box>
  );
}
